export type DocumentDiffCopy = {
  changes: string;
  onlyChanges: string;
  fullText: string;
  diffView: string;
  stats: (changed: number, added: number, removed: number) => string;
  unchanged: (count: number) => string;
  noChanges: string;
  loading: string;
  previousUnavailable: string;
  notShown: string;
  added: string;
  removed: string;
};

type Row = [
  string,
  string,
  string,
  (changed: number, added: number, removed: number) => string,
  (count: number) => string,
  string,
  string,
  string,
  string,
  string,
  string,
];
const rows: Record<string, Row> = {
  en: [
    'Only changes',
    'Full text',
    'Diff view',
    (c, a, r) => `${c} paragraphs changed · +${a} −${r}`,
    (c) => `${c} unchanged ${c === 1 ? 'paragraph' : 'paragraphs'}`,
    'No changes.',
    'Loading previous version…',
    "Couldn't load the previous version.",
    'The rest of this document is not shown.',
    'Added: ',
    'Removed: ',
  ],
  sv: [
    'Endast ändringar',
    'Fullständig text',
    'Jämförelse',
    (c, a, r) => `${c} ändrade stycken · +${a} −${r}`,
    (c) => `${c} oförändrade ${c === 1 ? 'stycke' : 'stycken'}`,
    'Inga ändringar.',
    'Laddar föregående version…',
    'Den föregående versionen kunde inte laddas.',
    'Resten av dokumentet visas inte.',
    'Tillagt: ',
    'Borttaget: ',
  ],
  da: [
    'Kun ændringer',
    'Fuld tekst',
    'Forskelvisning',
    (c, a, r) => `${c} ændrede afsnit · +${a} −${r}`,
    (c) => `${c} uændrede afsnit`,
    'Ingen ændringer.',
    'Indlæser forrige version…',
    'Den forrige version kunne ikke indlæses.',
    'Resten af dokumentet vises ikke.',
    'Tilføjet: ',
    'Fjernet: ',
  ],
  de: [
    'Nur Änderungen',
    'Vollständiger Text',
    'Vergleich',
    (c, a, r) => `${c} geänderte Absätze · +${a} −${r}`,
    (c) => `${c} unveränderte Absätze`,
    'Keine Änderungen.',
    'Vorherige Version wird geladen…',
    'Die vorherige Version konnte nicht geladen werden.',
    'Der Rest des Dokuments wird nicht angezeigt.',
    'Hinzugefügt: ',
    'Entfernt: ',
  ],
  es: [
    'Solo cambios',
    'Texto completo',
    'Comparación',
    (c, a, r) => `${c} párrafos cambiados · +${a} −${r}`,
    (c) => `${c} párrafos sin cambios`,
    'Sin cambios.',
    'Cargando la versión anterior…',
    'No se pudo cargar la versión anterior.',
    'El resto del documento no se muestra.',
    'Añadido: ',
    'Eliminado: ',
  ],
  fr: [
    'Modifications uniquement',
    'Texte complet',
    'Comparaison',
    (c, a, r) => `${c} paragraphes modifiés · +${a} −${r}`,
    (c) => `${c} paragraphes inchangés`,
    'Aucune modification.',
    'Chargement de la version précédente…',
    'Impossible de charger la version précédente.',
    'Le reste du document n’est pas affiché.',
    'Ajouté : ',
    'Supprimé : ',
  ],
  it: [
    'Solo modifiche',
    'Testo completo',
    'Confronto',
    (c, a, r) => `${c} paragrafi modificati · +${a} −${r}`,
    (c) => `${c} paragrafi invariati`,
    'Nessuna modifica.',
    'Caricamento della versione precedente…',
    'Impossibile caricare la versione precedente.',
    'Il resto del documento non viene mostrato.',
    'Aggiunto: ',
    'Rimosso: ',
  ],
  pt: [
    'Apenas alterações',
    'Texto completo',
    'Comparação',
    (c, a, r) => `${c} parágrafos alterados · +${a} −${r}`,
    (c) => `${c} parágrafos sem alterações`,
    'Sem alterações.',
    'A carregar a versão anterior…',
    'Não foi possível carregar a versão anterior.',
    'O resto do documento não é mostrado.',
    'Adicionado: ',
    'Removido: ',
  ],
  nl: [
    'Alleen wijzigingen',
    'Volledige tekst',
    'Vergelijking',
    (c, a, r) => `${c} gewijzigde alinea’s · +${a} −${r}`,
    (c) => `${c} ongewijzigde alinea’s`,
    'Geen wijzigingen.',
    'Vorige versie wordt geladen…',
    'De vorige versie kon niet worden geladen.',
    'De rest van het document wordt niet getoond.',
    'Toegevoegd: ',
    'Verwijderd: ',
  ],
  pl: [
    'Tylko zmiany',
    'Pełny tekst',
    'Porównanie',
    (c, a, r) => `${c} zmienionych akapitów · +${a} −${r}`,
    (c) => `${c} niezmienionych akapitów`,
    'Brak zmian.',
    'Wczytywanie poprzedniej wersji…',
    'Nie udało się wczytać poprzedniej wersji.',
    'Reszta dokumentu nie jest pokazana.',
    'Dodano: ',
    'Usunięto: ',
  ],
  cs: [
    'Jen změny',
    'Celý text',
    'Porovnání',
    (c, a, r) => `${c} změněných odstavců · +${a} −${r}`,
    (c) => `${c} nezměněných odstavců`,
    'Žádné změny.',
    'Načítání předchozí verze…',
    'Předchozí verzi se nepodařilo načíst.',
    'Zbytek dokumentu není zobrazen.',
    'Přidáno: ',
    'Odebráno: ',
  ],
  sk: [
    'Iba zmeny',
    'Celý text',
    'Porovnanie',
    (c, a, r) => `${c} zmenených odsekov · +${a} −${r}`,
    (c) => `${c} nezmenených odsekov`,
    'Žiadne zmeny.',
    'Načítava sa predchádzajúca verzia…',
    'Predchádzajúcu verziu sa nepodarilo načítať.',
    'Zvyšok dokumentu sa nezobrazuje.',
    'Pridané: ',
    'Odstránené: ',
  ],
  fi: [
    'Vain muutokset',
    'Koko teksti',
    'Vertailu',
    (c, a, r) => `${c} muuttunutta kappaletta · +${a} −${r}`,
    (c) => `${c} muuttumatonta kappaletta`,
    'Ei muutoksia.',
    'Ladataan edellistä versiota…',
    'Edellistä versiota ei voitu ladata.',
    'Dokumentin loppuosaa ei näytetä.',
    'Lisätty: ',
    'Poistettu: ',
  ],
  et: [
    'Ainult muudatused',
    'Kogu tekst',
    'Võrdlus',
    (c, a, r) => `${c} muudetud lõiku · +${a} −${r}`,
    (c) => `${c} muutmata lõiku`,
    'Muudatusi pole.',
    'Eelmise versiooni laadimine…',
    'Eelmist versiooni ei saanud laadida.',
    'Dokumendi ülejäänud osa ei ole näha.',
    'Lisatud: ',
    'Eemaldatud: ',
  ],
  lv: [
    'Tikai izmaiņas',
    'Pilns teksts',
    'Salīdzinājums',
    (c, a, r) => `${c} mainītas rindkopas · +${a} −${r}`,
    (c) => `${c} nemainītas rindkopas`,
    'Izmaiņu nav.',
    'Ielādē iepriekšējo versiju…',
    'Neizdevās ielādēt iepriekšējo versiju.',
    'Dokumenta pārējā daļa nav parādīta.',
    'Pievienots: ',
    'Noņemts: ',
  ],
  lt: [
    'Tik pakeitimai',
    'Visas tekstas',
    'Palyginimas',
    (c, a, r) => `${c} pakeistos pastraipos · +${a} −${r}`,
    (c) => `${c} nepakeistos pastraipos`,
    'Pakeitimų nėra.',
    'Įkeliama ankstesnė versija…',
    'Ankstesnės versijos įkelti nepavyko.',
    'Likusi dokumento dalis nerodoma.',
    'Pridėta: ',
    'Pašalinta: ',
  ],
  hu: [
    'Csak a változások',
    'Teljes szöveg',
    'Összehasonlítás',
    (c, a, r) => `${c} módosított bekezdés · +${a} −${r}`,
    (c) => `${c} változatlan bekezdés`,
    'Nincs változás.',
    'Előző verzió betöltése…',
    'Az előző verzió nem tölthető be.',
    'A dokumentum többi része nem látható.',
    'Hozzáadva: ',
    'Eltávolítva: ',
  ],
  ro: [
    'Doar modificări',
    'Text complet',
    'Comparație',
    (c, a, r) => `${c} paragrafe modificate · +${a} −${r}`,
    (c) => `${c} paragrafe neschimbate`,
    'Nu există modificări.',
    'Se încarcă versiunea anterioară…',
    'Versiunea anterioară nu a putut fi încărcată.',
    'Restul documentului nu este afișat.',
    'Adăugat: ',
    'Eliminat: ',
  ],
  bg: [
    'Само промените',
    'Пълен текст',
    'Сравнение',
    (c, a, r) => `${c} променени абзаца · +${a} −${r}`,
    (c) => `${c} непроменени абзаца`,
    'Няма промени.',
    'Зареждане на предишната версия…',
    'Предишната версия не може да се зареди.',
    'Останалата част не се показва.',
    'Добавено: ',
    'Премахнато: ',
  ],
  el: [
    'Μόνο αλλαγές',
    'Πλήρες κείμενο',
    'Σύγκριση',
    (c, a, r) => `${c} αλλαγμένες παράγραφοι · +${a} −${r}`,
    (c) => `${c} αμετάβλητες παράγραφοι`,
    'Δεν υπάρχουν αλλαγές.',
    'Φόρτωση προηγούμενης έκδοσης…',
    'Δεν ήταν δυνατή η φόρτωση της προηγούμενης έκδοσης.',
    'Το υπόλοιπο έγγραφο δεν εμφανίζεται.',
    'Προστέθηκε: ',
    'Αφαιρέθηκε: ',
  ],
  hr: [
    'Samo promjene',
    'Cijeli tekst',
    'Usporedba',
    (c, a, r) => `${c} promijenjenih odlomaka · +${a} −${r}`,
    (c) => `${c} nepromijenjenih odlomaka`,
    'Nema promjena.',
    'Učitavanje prethodne verzije…',
    'Prethodnu verziju nije moguće učitati.',
    'Ostatak dokumenta nije prikazan.',
    'Dodano: ',
    'Uklonjeno: ',
  ],
  sl: [
    'Samo spremembe',
    'Celotno besedilo',
    'Primerjava',
    (c, a, r) => `${c} spremenjenih odstavkov · +${a} −${r}`,
    (c) => `${c} nespremenjenih odstavkov`,
    'Ni sprememb.',
    'Nalaganje prejšnje različice…',
    'Prejšnje različice ni bilo mogoče naložiti.',
    'Preostanek dokumenta ni prikazan.',
    'Dodano: ',
    'Odstranjeno: ',
  ],
  ga: [
    'Athruithe amháin',
    'Téacs iomlán',
    'Comparáid',
    (c, a, r) => `${c} alt athraithe · +${a} −${r}`,
    (c) => `${c} alt gan athrú`,
    'Gan athruithe.',
    'Leagan roimhe seo á luchtú…',
    'Níorbh fhéidir an leagan roimhe seo a luchtú.',
    'Ní thaispeántar an chuid eile den doiciméad.',
    'Curtha leis: ',
    'Bainte: ',
  ],
  mt: [
    'Bidliet biss',
    'Test sħiħ',
    'Tqabbil',
    (c, a, r) => `${c} paragrafi mibdula · +${a} −${r}`,
    (c) => `${c} paragrafi mhux mibdula`,
    'M’hemmx bidliet.',
    'Qed jitgħabba l-verżjoni ta’ qabel…',
    'Ma setax jitgħabba l-verżjoni ta’ qabel.',
    'Il-bqija tad-dokument ma jintweriex.',
    'Miżjud: ',
    'Imneħħi: ',
  ],
};

const changesLabels: Record<string, string> = {
  en: 'Changes',
  sv: 'Ändringar',
  da: 'Ændringer',
  de: 'Änderungen',
  es: 'Cambios',
  fr: 'Modifications',
  it: 'Modifiche',
  pt: 'Alterações',
  nl: 'Wijzigingen',
  pl: 'Zmiany',
  cs: 'Změny',
  sk: 'Zmeny',
  fi: 'Muutokset',
  et: 'Muudatused',
  lv: 'Izmaiņas',
  lt: 'Pakeitimai',
  hu: 'Változások',
  ro: 'Modificări',
  bg: 'Промени',
  el: 'Αλλαγές',
  hr: 'Promjene',
  sl: 'Spremembe',
  ga: 'Athruithe',
  mt: 'Bidliet',
};

export const DOCUMENT_DIFF_LOCALES = Object.freeze(Object.keys(rows));
export function documentDiffCopy(locale: string): DocumentDiffCopy {
  const row = rows[locale.toLowerCase().split(/[-_]/, 1)[0]] ?? rows.en;
  const [
    onlyChanges,
    fullText,
    diffView,
    stats,
    unchanged,
    noChanges,
    loading,
    previousUnavailable,
    notShown,
    added,
    removed,
  ] = row;
  return {
    changes: changesLabels[locale.toLowerCase().split(/[-_]/, 1)[0]] ?? changesLabels.en,
    onlyChanges,
    fullText,
    diffView,
    stats,
    unchanged,
    noChanges,
    loading,
    previousUnavailable,
    notShown,
    added,
    removed,
  };
}
